import type { PrimitiveFeature, BooleanFeature, ModifierFeature, ProfileType, TransformFeature } from "./featureTypes";
import { SHAPE_ICONS } from "./shapeIcons";

interface CreationToolbarProps {
  onAdd: (type: PrimitiveFeature["type"]) => void;
  onAddExtrude: (profile: ProfileType) => void;
  onAddSketch: () => void;
  onAddLoft: () => void;
  onAddRevolve: (profile: ProfileType) => void;
  onAddSweep: () => void;
  onAddBoolean: (type: BooleanFeature["type"]) => void;
  onAddModifier: (type: ModifierFeature["type"]) => void;
  onAddTransform: (type: TransformFeature["type"]) => void;
  onAddImportedPart?: () => void;
  onStartMate?: () => void;
  onAddAssemblyPattern?: (type: "assemblyLinearPattern" | "assemblyCircularPattern") => void;
  onCheckInterference?: () => void;
  onAddSheetMetalBase?: () => void;
  onAddSheetMetalFlange?: () => void;
  onAddSheetMetalCornerRelief?: () => void;
  mateMode?: boolean;
  booleanDisabled: boolean;
  modifierDisabled: boolean;
  transformDisabled: boolean;
  assemblyPatternDisabled?: boolean;
  sheetMetalFlangeDisabled?: boolean;
  isAssembly?: boolean;
}

const SHAPES: { type: PrimitiveFeature["type"]; label: string }[] = [
  { type: "box", label: "Box" },
  { type: "cylinder", label: "Cylinder" },
  { type: "sphere", label: "Sphere" },
];

const BOOLEANS: { type: BooleanFeature["type"]; label: string }[] = [
  { type: "cut", label: "Cut" },
  { type: "fuse", label: "Fuse" },
  { type: "intersect", label: "Intersect" },
];

const MODIFIERS: { type: ModifierFeature["type"]; label: string }[] = [
  { type: "fillet", label: "Fillet" },
  { type: "chamfer", label: "Chamfer" },
  { type: "shell", label: "Shell" },
  { type: "draft", label: "Draft" },
];

const PROFILES: { profile: ProfileType; label: string }[] = [
  { profile: "rectangle", label: "Rect" },
  { profile: "circle", label: "Circle" },
  { profile: "rounded-rectangle", label: "Round" },
];

const TRANSFORMS: { type: TransformFeature["type"]; label: string }[] = [
  { type: "mirror", label: "Mirror" },
  { type: "linearPattern", label: "Linear" },
  { type: "circularPattern", label: "Circular" },
];

export default function CreationToolbar({
  onAdd, onAddExtrude, onAddSketch, onAddLoft, onAddRevolve, onAddSweep, onAddBoolean, onAddModifier, onAddTransform,
  onAddImportedPart, onStartMate, onAddAssemblyPattern, onCheckInterference,
  onAddSheetMetalBase, onAddSheetMetalFlange, onAddSheetMetalCornerRelief,
  mateMode, isAssembly,
  booleanDisabled, modifierDisabled, transformDisabled, assemblyPatternDisabled, sheetMetalFlangeDisabled,
}: CreationToolbarProps) {
  return (
    <div className="creation-toolbar">
      <div className="creation-group">
        <span className="creation-group-label">Primitives</span>
        <div className="creation-group-buttons">
          {SHAPES.map((s) => (
            <button key={s.type} className="creation-btn" onClick={() => onAdd(s.type)} title={`Add ${s.label}`}>
              {SHAPE_ICONS[s.type]}
              <span className="creation-btn-label">{s.label}</span>
            </button>
          ))}
        </div>
      </div>
      <div className="creation-toolbar-divider" />
      <div className="creation-group">
        <span className="creation-group-label">Extrude</span>
        <div className="creation-group-buttons">
          {PROFILES.map((p) => (
            <button key={p.profile} className="creation-btn" onClick={() => onAddExtrude(p.profile)} title={`Extrude ${p.label} profile`}>
              {SHAPE_ICONS["extrude"]}
              <span className="creation-btn-label">{p.label}</span>
            </button>
          ))}
          <button className="creation-btn" onClick={onAddSketch} title="Sketch custom profile">
            {SHAPE_ICONS["sketch"]}
            <span className="creation-btn-label">Sketch</span>
          </button>
        </div>
      </div>
      <div className="creation-toolbar-divider" />
      <div className="creation-group">
        <span className="creation-group-label">Revolve</span>
        <div className="creation-group-buttons">
          {PROFILES.map((p) => (
            <button key={p.profile} className="creation-btn" onClick={() => onAddRevolve(p.profile)} title={`Revolve ${p.label} profile`}>
              {SHAPE_ICONS["revolve"]}
              <span className="creation-btn-label">{p.label}</span>
            </button>
          ))}
          <button className="creation-btn" onClick={onAddSweep} title="Helix sweep">
            {SHAPE_ICONS["sweep"]}
            <span className="creation-btn-label">Helix</span>
          </button>
          <button className="creation-btn" onClick={onAddLoft} title="Loft between profiles">
            {SHAPE_ICONS["loft"]}
            <span className="creation-btn-label">Loft</span>
          </button>
        </div>
      </div>
      <div className="creation-toolbar-divider" />
      <div className="creation-group">
        <span className="creation-group-label">Booleans</span>
        <div className="creation-group-buttons">
          {BOOLEANS.map((b) => (
            <button key={b.type} className="creation-btn" onClick={() => onAddBoolean(b.type)} title={`${b.label} two shapes`} disabled={booleanDisabled}>
              {SHAPE_ICONS[b.type]}
              <span className="creation-btn-label">{b.label}</span>
            </button>
          ))}
        </div>
      </div>
      <div className="creation-toolbar-divider" />
      <div className="creation-group">
        <span className="creation-group-label">Modify</span>
        <div className="creation-group-buttons">
          {MODIFIERS.map((m) => (
            <button key={m.type} className="creation-btn" onClick={() => onAddModifier(m.type)} title={`${m.label} edges/faces`} disabled={modifierDisabled}>
              {SHAPE_ICONS[m.type]}
              <span className="creation-btn-label">{m.label}</span>
            </button>
          ))}
        </div>
      </div>
      <div className="creation-toolbar-divider" />
      <div className="creation-group">
        <span className="creation-group-label">Transform</span>
        <div className="creation-group-buttons">
          {TRANSFORMS.map((t) => (
            <button key={t.type} className="creation-btn" onClick={() => onAddTransform(t.type)} title={`${t.label} transform`} disabled={transformDisabled}>
              {SHAPE_ICONS[t.type]}
              <span className="creation-btn-label">{t.label}</span>
            </button>
          ))}
        </div>
      </div>
      {onAddSheetMetalBase && (
        <>
          <div className="creation-toolbar-divider" />
          <div className="creation-group">
            <span className="creation-group-label">Sheet Metal</span>
            <div className="creation-group-buttons">
              <button className="creation-btn" onClick={onAddSheetMetalBase} title="Sheet metal base flange">
                <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.2">
                  <path d="M2 10h12v2H2zM2 10V6h12v4"/>
                </svg>
                <span className="creation-btn-label">Base</span>
              </button>
              {onAddSheetMetalFlange && (
                <button className="creation-btn" onClick={onAddSheetMetalFlange} title="Add edge flange" disabled={sheetMetalFlangeDisabled}>
                  <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.2">
                    <path d="M2 10h8v2H2zM10 10V4h2v8H10"/>
                  </svg>
                  <span className="creation-btn-label">Flange</span>
                </button>
              )}
              {onAddSheetMetalCornerRelief && (
                <button className="creation-btn" onClick={onAddSheetMetalCornerRelief} title="Corner relief" disabled={sheetMetalFlangeDisabled}>
                  <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.2">
                    <path d="M4 12h8V4"/><circle cx="4" cy="4" r="2.5"/>
                  </svg>
                  <span className="creation-btn-label">Relief</span>
                </button>
              )}
            </div>
          </div>
        </>
      )}
      {isAssembly && onAddImportedPart && (
        <>
          <div className="creation-toolbar-divider" />
          <div className="creation-group">
            <span className="creation-group-label">Assembly</span>
            <div className="creation-group-buttons">
              <button className="creation-btn" onClick={onAddImportedPart} title="Add part to assembly">
                {SHAPE_ICONS["importedPart"]}
                <span className="creation-btn-label">Add Part</span>
              </button>
              {onStartMate && (
                <button className={`creation-btn ${mateMode ? "active" : ""}`} onClick={onStartMate} title="Mate faces">
                  <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.2">
                    <path d="M3 4h4v8H3zM9 4h4v8H9z" />
                    <path d="M7 8h2" strokeWidth="2" />
                  </svg>
                  <span className="creation-btn-label">Mate</span>
                </button>
              )}
              {onAddAssemblyPattern && (
                <>
                  <button className="creation-btn" onClick={() => onAddAssemblyPattern("assemblyLinearPattern")} title="Linear array of imported part" disabled={assemblyPatternDisabled}>
                    <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.2">
                      <rect x="1" y="5" width="4" height="6" rx="0.5"/><rect x="6" y="5" width="4" height="6" rx="0.5"/><rect x="11" y="5" width="4" height="6" rx="0.5"/>
                    </svg>
                    <span className="creation-btn-label">Array</span>
                  </button>
                  <button className="creation-btn" onClick={() => onAddAssemblyPattern("assemblyCircularPattern")} title="Circular array of imported part" disabled={assemblyPatternDisabled}>
                    <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.2">
                      <rect x="6.5" y="1" width="3" height="4" rx="0.5"/><rect x="11" y="6.5" width="3" height="4" rx="0.5" transform="rotate(60 12.5 8.5)"/><rect x="2" y="6.5" width="3" height="4" rx="0.5" transform="rotate(-60 3.5 8.5)"/>
                    </svg>
                    <span className="creation-btn-label">Radial</span>
                  </button>
                </>
              )}
              {onCheckInterference && (
                <button className="creation-btn" onClick={onCheckInterference} title="Check interference between parts" disabled={assemblyPatternDisabled}>
                  <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.2">
                    <circle cx="6" cy="8" r="4"/><circle cx="10" cy="8" r="4"/><path d="M8 5.5v5" strokeWidth="1.5" strokeDasharray="1.5 1"/>
                  </svg>
                  <span className="creation-btn-label">Check</span>
                </button>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
